import { useEffect, useMemo, useState } from 'react';
import { FileBarChart } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { getEmpresa } from '../services/empresaService';
import { getObras } from '../services/obraService';
import { getMedicaoMoldagem, getMedicaoRuptura } from '../services/medicaoService';
import { normalizeEmpresa, normalizeObra } from '../utils/relatorioNormalizers';
import { formatDateBr } from '../utils/formatters';
import MedicaoMoldagemPDF from '../components/MedicaoMoldagemPDF';
import MedicaoRupturaPDF from '../components/MedicaoRupturaPDF';
import usePersonalizacaoRelatorio from '../hooks/usePersonalizacaoRelatorio';

const CONFIG = {
  moldagem: {
    titulo: 'Medição - Moldagem',
    descricao: 'Selecione empresa, obra e período de concretagem para gerar a medição de CPs moldados.',
    fetch: getMedicaoMoldagem,
    Pdf: MedicaoMoldagemPDF,
    arquivo: 'medicao-moldagem',
  },
  ruptura: {
    titulo: 'Medição - Ruptura',
    descricao: 'Selecione empresa, obra e período de ruptura para gerar a medição de CPs rompidos e previstos.',
    fetch: getMedicaoRuptura,
    Pdf: MedicaoRupturaPDF,
    arquivo: 'medicao-ruptura',
  },
};

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60';

// Relatório de medição (moldagem ou ruptura): empresa + obra + período -> PDF.
function MedicaoRelatorioPage({ tipo }) {
  const config = CONFIG[tipo];
  const { Pdf } = config;

  const [empresas, setEmpresas] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedEmpresaId, setSelectedEmpresaId] = useState('');
  const [selectedObraId, setSelectedObraId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const [dados, setDados] = useState(null);
  const [loadingDados, setLoadingDados] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      const [empresasResult, obrasResult] = await Promise.allSettled([
        getEmpresa(),
        getObras(),
      ]);
      if (!isMounted) return;
      if (empresasResult.status === 'fulfilled' && Array.isArray(empresasResult.value)) {
        setEmpresas(empresasResult.value.map(normalizeEmpresa).filter((e) => e.id));
      }
      if (obrasResult.status === 'fulfilled' && Array.isArray(obrasResult.value)) {
        setObras(obrasResult.value.map(normalizeObra).filter((o) => o.id));
      }
      setLoading(false);
    };
    loadData().catch(() => {
      if (isMounted) setLoading(false);
    });
    return () => { isMounted = false; };
  }, []);

  const obrasDaEmpresa = useMemo(
    () => (selectedEmpresaId ? obras.filter((o) => o.idEmpresa === selectedEmpresaId) : []),
    [obras, selectedEmpresaId]
  );

  const periodoInvalido = Boolean(dataInicio && dataFim && dataInicio > dataFim);
  const canGerar = Boolean(selectedEmpresaId && selectedObraId && dataInicio && dataFim && !periodoInvalido);

  const handleEmpresaChange = (event) => {
    setSelectedEmpresaId(event.target.value);
    setSelectedObraId('');
  };

  const handleObraChange = (event) => {
    setSelectedObraId(event.target.value);
  };

  // Busca os dados da medição sempre que empresa/obra/período mudam e estão completos.
  useEffect(() => {
    if (!canGerar) {
      setDados(null);
      return undefined;
    }
    let isMounted = true;
    setLoadingDados(true);
    setErro('');
    config
      .fetch({ dataInicio, dataFim, idObras: [selectedObraId], idEmpresas: [selectedEmpresaId] })
      .then((data) => { if (isMounted) setDados(data); })
      .catch((error) => {
        if (!isMounted) return;
        setDados(null);
        setErro(error?.message || 'Não foi possível carregar os dados da medição.');
      })
      .finally(() => { if (isMounted) setLoadingDados(false); });
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canGerar, dataInicio, dataFim, selectedObraId, selectedEmpresaId, tipo]);

  const empresaSelecionada = empresas.find((e) => e.id === selectedEmpresaId);
  const {
    logoCliente,
    assinaturas,
    carregando: carregandoPersonalizacao,
  } = usePersonalizacaoRelatorio({
    idUsuario1: empresaSelecionada?.idUsuarioAssinatura1,
    idUsuario2: empresaSelecionada?.idUsuarioAssinatura2,
  });

  const empresaNome = empresaSelecionada?.nome || '';
  const obraNome = obras.find((o) => o.id === selectedObraId)?.nome || '';
  const periodo = canGerar ? `${formatDateBr(dataInicio)} a ${formatDateBr(dataFim)}` : '';
  const semDados = !loadingDados && dados && (dados.cps?.length ?? 0) === 0;
  const desabilitado = loadingDados || carregandoPersonalizacao || !dados || semDados;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">{config.titulo}</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">{config.descricao}</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="med-empresa" className="text-sm font-medium text-slate-700">
              Empresa <span className="text-rose-500">*</span>
            </label>
            <select id="med-empresa" value={selectedEmpresaId} onChange={handleEmpresaChange} disabled={loading} className={inputClass}>
              <option value="">{loading ? 'Carregando...' : 'Selecione'}</option>
              {empresas.map((empresa) => (
                <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="med-obra" className="text-sm font-medium text-slate-700">
              Obra <span className="text-rose-500">*</span>
            </label>
            <select id="med-obra" value={selectedObraId} onChange={handleObraChange} disabled={!selectedEmpresaId} className={inputClass}>
              <option value="">
                {!selectedEmpresaId
                  ? 'Selecione uma empresa primeiro'
                  : obrasDaEmpresa.length === 0
                    ? 'Nenhuma obra para esta empresa'
                    : 'Selecione'}
              </option>
              {obrasDaEmpresa.map((obra) => (
                <option key={obra.id} value={obra.id}>{obra.nome}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="med-inicio" className="text-sm font-medium text-slate-700">
              Data inicial <span className="text-rose-500">*</span>
            </label>
            <input id="med-inicio" type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className={inputClass} />
          </div>

          <div className="space-y-2">
            <label htmlFor="med-fim" className="text-sm font-medium text-slate-700">
              Data final <span className="text-rose-500">*</span>
            </label>
            <input id="med-fim" type="date" value={dataFim} min={dataInicio || undefined} onChange={(e) => setDataFim(e.target.value)} className={inputClass} />
            {periodoInvalido ? <p className="text-xs text-rose-600">A data final deve ser igual ou posterior à inicial.</p> : null}
          </div>

          <p className="text-xs text-slate-400 sm:col-span-2">As assinaturas do rodapé vêm dos usuários vinculados no cadastro da empresa.</p>
        </div>
      </section>

      {canGerar ? (
        <div className="flex flex-col items-end gap-2">
          {erro ? <p className="text-sm text-rose-600">{erro}</p> : null}
          {semDados ? <p className="text-sm text-slate-500">Nenhum corpo de prova encontrado para o período.</p> : null}
          <PDFDownloadLink
            document={(
              <Pdf
                resumo={dados?.resumo}
                cps={dados?.cps ?? []}
                empresaNome={empresaNome}
                obraNome={obraNome}
                periodo={periodo}
                logoCliente={logoCliente}
                assinaturas={assinaturas}
              />
            )}
            fileName={`${config.arquivo}-${selectedObraId}-${dataInicio}-${dataFim}.pdf`}
            style={{ textDecoration: 'none', pointerEvents: desabilitado ? 'none' : 'auto' }}
            className={`inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 ${desabilitado ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {({ loading: gerando, error }) => (
              <>
                <FileBarChart className="h-4 w-4" />
                {carregandoPersonalizacao
                  ? 'Preparando...'
                  : gerando || loadingDados
                    ? 'Gerando...'
                    : error
                      ? 'Falha ao gerar PDF'
                      : 'Gerar relatório'}
              </>
            )}
          </PDFDownloadLink>
        </div>
      ) : null}
    </div>
  );
}

export default MedicaoRelatorioPage;
